import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { COUNTRY_CONFIGS, formatCurrency } from '../../utils/formatters';

const API = '/api';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
    phone: ['M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z'],
    mapPin: ['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z', 'M12 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6z'],
    calendar: ['M19 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z', 'M16 2v4', 'M8 2v4', 'M3 10h18'],
    clock: ['M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z', 'M12 6v6l4 2'],
    truck: ['M7 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M17 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M5 17H3V7c0-1.1.9-2 2-2h11v12h-2', 'M19 17h2v-6l-3-4h-2v7h4'],
    check: ['M20 6L9 17l-5-5'],
    loader: ['M12 2v4', 'M12 18v4', 'M4.93 4.93l2.83 2.83', 'M16.24 16.24l2.83 2.83', 'M2 12h4', 'M18 12h4', 'M4.93 19.07l2.83-2.83', 'M16.24 7.76l2.83-2.83']
};

export default function AdhocTrips({ user, operatorDetails }) {
    const [form, setForm] = useState({
        passengers: [{ passengerName: '', passengerPhone: '', passengerEmail: '' }],
        passengerName: '',
        passengerPhone: '',
        countryCode: COUNTRY_CONFIGS[operatorDetails?.country]?.phoneCode || '+91',
        passengerEmail: '',
        pickupLocation: '',
        dropLocation: '',
        tripDate: new Date().toISOString().split('T')[0],
        pickupTime: '',
        dropTime: '',
        vehicleId: '',
        driverId: '',
        vendorId: '',
        isVendorMode: false,
        amount: '',
        tripType: 'on_demand',
        tripFrequency: 'One Time',
        status: 'Scheduled'
    });

    const [showPriceModal, setShowPriceModal] = useState(null);
    const [showAssignModal, setShowAssignModal] = useState(null);
    const [showAllTrips, setShowAllTrips] = useState(false);

    const [vehicles, setVehicles] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [vendorFleet, setVendorFleet] = useState([]);
    const [adhocTrips, setAdhocTrips] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);
    const [success, setSuccess] = useState(null);

    const operatorId = user?.operatorId || user?._id;
    const pickupInputRef = useRef(null);
    const dropInputRef = useRef(null);

    useEffect(() => {
        fetchResources();
    }, []);

    useEffect(() => {
        if (!window.google || fetchingData) return;

        // Timeout to ensure refs are attached after render
        const timer = setTimeout(() => {
            if (pickupInputRef.current) {
                const pickupAutocomplete = new window.google.maps.places.Autocomplete(pickupInputRef.current);
                pickupAutocomplete.addListener('place_changed', () => {
                    const place = pickupAutocomplete.getPlace();
                    if (place.geometry) {
                        setForm(prev => ({ 
                            ...prev, 
                            pickupLocation: place.formatted_address || place.name,
                            pickupLat: place.geometry.location.lat(),
                            pickupLng: place.geometry.location.lng()
                        }));
                    }
                });
            }

            if (dropInputRef.current) {
                const dropAutocomplete = new window.google.maps.places.Autocomplete(dropInputRef.current);
                dropAutocomplete.addListener('place_changed', () => {
                    const place = dropAutocomplete.getPlace();
                    if (place.geometry) {
                        setForm(prev => ({ 
                            ...prev, 
                            dropLocation: place.formatted_address || place.name,
                            dropLat: place.geometry.location.lat(),
                            dropLng: place.geometry.location.lng()
                        }));
                    }
                });
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [fetchingData]);

    const fetchResources = async () => {
        try {
            const [vRes, dRes, vnRes, tRes, vfRes] = await Promise.all([
                axios.get(`${API}/vehicles/${operatorId}`),
                axios.get(`${API}/drivers/${operatorId}`),
                axios.get(`${API}/vendors/${operatorId}`),
                axios.get(`${API}/trips/${operatorId}`),
                axios.get(`${API}/vendor-fleet/${operatorId}`)
            ]);
            setVehicles(vRes.data);
            setDrivers(dRes.data);
            setVendors(vnRes.data);
            setVendorFleet(vfRes.data);
            setAdhocTrips(tRes.data.filter(t => t.tripType === 'on_demand'));
        } catch (err) {
            console.error("Error fetching resources:", err);
        } finally {
            setFetchingData(false);
        }
    };

    const handleVehicleChange = (vId) => {
        if (form.isVendorMode) {
            // Selected a vendor fleet vehicle
            setForm(prev => ({ ...prev, vehicleId: vId }));
        } else {
            // Selected an internal vehicle - auto assign driver/vendor if linked
            const vehicle = vehicles.find(v => v._id === vId);
            if (vehicle) {
                setForm(prev => ({
                    ...prev,
                    vehicleId: vId,
                    driverId: vehicle.driverId?._id || vehicle.driverId || prev.driverId,
                    vendorId: vehicle.vendorId?._id || vehicle.vendorId || ''
                }));
            } else {
                setForm(prev => ({ ...prev, vehicleId: vId }));
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.passengers[0].passengerName || !form.passengers[0].passengerPhone || !form.pickupLocation || !form.dropLocation || !form.tripDate || !form.pickupTime || !form.vehicleId) {
            alert("Please fill all required fields.");
            return;
        }

        setLoading(true);
        try {
            const passengerName = form.passengers.map(p => p.passengerName).join(', ');
            const passengerPhone = form.passengers.map(p => `${form.countryCode}${p.passengerPhone}`).join(', ');
            const passengerEmail = form.passengers.map(p => p.passengerEmail).filter(Boolean).join(', ');
            const payload = {
                ...form,
                operatorId,
                passengerName,
                passengerPhone,
                passengerEmail,
                startTime: form.pickupTime,
                endTime: form.dropTime || ''
            };
            await axios.post(`${API}/trips`, payload);
            setSuccess(true);
            setForm({
                passengers: [{ passengerName: '', passengerPhone: '', passengerEmail: '' }],
                passengerName: '',
                passengerPhone: '',
                countryCode: COUNTRY_CONFIGS[operatorDetails?.country]?.phoneCode || '+91',
                passengerEmail: '',
                pickupLocation: '',
                dropLocation: '',
                tripDate: new Date().toISOString().split('T')[0],
                pickupTime: '',
                dropTime: '',
                vehicleId: '',
                driverId: '',
                vendorId: '',
                isVendorMode: false,
                amount: '',
                tripType: 'on_demand',
                tripFrequency: 'One Time',
                status: 'Scheduled'
            });
            fetchResources(); // Refresh list
            setTimeout(() => setSuccess(null), 5000);
        } catch (err) {
            console.error("Error creating adhoc trip:", err);
            alert("Failed to create trip. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (tripId, status, additionalData = {}) => {
        try {
            await axios.put(`${API}/trips/${tripId}`, { status, ...additionalData });
            fetchResources();
            if (status === 'Scheduled') setShowAssignModal(null);
        } catch (err) {
            console.error("Error updating trip status:", err);
            alert("Failed to update trip status.");
        }
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Requested': return { bg: '#fef3c7', color: '#92400e', label: 'Requested' };
            case 'PriceProposed': return { bg: '#ede9fe', color: '#5b21b6', label: 'Price Proposed' };
            case 'ClientAccepted': return { bg: '#d1fae5', color: '#065f46', label: 'Client Accepted' };
            case 'ClientRejected': return { bg: '#fee2e2', color: '#991b1b', label: 'Client Rejected' };
            case 'ClientSuggested': return { bg: '#fef3c7', color: '#b45309', label: 'Counter Offer' };
            case 'Scheduled': return { bg: '#dcfce7', color: '#166534', label: 'Scheduled' };
            case 'Rejected': return { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' };
            case 'Completed': return { bg: '#e0f2fe', color: '#075985', label: 'Completed' };
            case 'Cancelled': return { bg: '#f1f5f9', color: '#475569', label: 'Cancelled' };
            default: return { bg: '#f1f5f9', color: '#475569', label: status };
        }
    };

    if (fetchingData) {
        return <div style={{ padding: 100, textAlign: 'center', color: '#64748b' }}>
            <div className="spinner" style={{ margin: '0 auto 20px', width: 40, height: 40, border: '3px solid #f3f4f6', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            Initializing Adhoc Portal...
        </div>;
    }

    const filteredVendorFleet = vendorFleet.filter(v => v.vendorId?._id === form.vendorId || v.vendorId === form.vendorId);

    return (
        <div className="md-page">
            <div className="md-header">
                <div>
                    <h1 className="md-title">Adhoc Trips</h1>
                    <p className="md-subtitle">Create and manage immediate trip requirements.</p>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '30px', alignItems: 'start' }}>
                {/* Left: Form Card */}
                <div className="md-panel" style={{ padding: '35px', borderRadius: '24px', boxShadow: '0 20px 50px rgba(0,0,0,0.05)', border: '1px solid rgba(255,255,255,0.8)', background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)' }}>
                    <form onSubmit={handleSubmit}>
                        <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1e1b4b', marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ padding: '8px', background: 'linear-gradient(135deg, #6366f1, #a855f7)', borderRadius: '12px', color: '#fff' }}>
                                <Icon d={icons.truck} size={20} />
                            </div>
                            Create New Adhoc Trip
                        </h2>

                        {success && (
                            <div style={{
                                marginBottom: '25px', padding: '15px 20px', borderRadius: '16px',
                                background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534',
                                display: 'flex', alignItems: 'center', gap: '12px', animation: 'fadeIn 0.4s ease'
                            }}>
                                <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#22c55e', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Icon d={icons.check} size={14} />
                                </div>
                                <span style={{ fontWeight: 600 }}>Trip successfully scheduled!</span>
                            </div>
                        )}

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            {/* Passenger Details */}
                            {form.passengers.map((p, idx) => (
                                <div key={idx} style={{ gridColumn: 'span 2', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', padding: '15px', background: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0', position: 'relative' }}>
                                    {idx > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setForm({ ...form, passengers: form.passengers.filter((_, i) => i !== idx) })}
                                            style={{ position: 'absolute', top: '10px', right: '10px', background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '8px', padding: '4px 8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
                                        >
                                            ✕ Remove
                                        </button>
                                    )}
                                    <div className="md-form-group">
                                        <label className="md-label">Passenger Name*</label>
                                        <div style={{ position: 'relative' }}>
                                            <div style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                                                <Icon d={icons.user} size={16} />
                                            </div>
                                            <input
                                                className="md-input"
                                                style={{ paddingLeft: '40px', height: '48px', borderRadius: '12px' }}
                                                placeholder="Full Name"
                                                value={p.passengerName}
                                                onChange={e => {
                                                    const newP = [...form.passengers];
                                                    newP[idx].passengerName = e.target.value;
                                                    setForm({ ...form, passengers: newP });
                                                }}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="md-form-group">
                                        <label className="md-label">Phone Number*</label>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <select
                                                className="md-input"
                                                style={{ width: '90px', height: '48px', borderRadius: '12px', textAlign: 'center', padding: '0 5px' }}
                                                value={form.countryCode}
                                                onChange={e => setForm({ ...form, countryCode: e.target.value })}
                                            >
                                                {(operatorDetails?.country ? [COUNTRY_CONFIGS[operatorDetails.country]].filter(Boolean) : Object.values(COUNTRY_CONFIGS)).map(config => (
                                                    <option key={config.phoneCode} value={config.phoneCode}>
                                                        {config.phoneCode}
                                                    </option>
                                                ))}
                                            </select>
                                            <div style={{ position: 'relative', flex: 1 }}>
                                                <div style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                                                    <Icon d={icons.phone} size={16} />
                                                </div>
                                                <input
                                                    className="md-input"
                                                    style={{ paddingLeft: '40px', height: '48px', borderRadius: '12px' }}
                                                    placeholder="Number"
                                                    value={p.passengerPhone}
                                                    onChange={e => {
                                                        const newP = [...form.passengers];
                                                        newP[idx].passengerPhone = e.target.value;
                                                        setForm({ ...form, passengers: newP });
                                                    }}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                        <label className="md-label">Email Address (Optional)</label>
                                        <div style={{ position: 'relative' }}>
                                            <div style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                                                <Icon d={icons.loader} size={16} /> {/* Using loader as generic email-ish icon if no mail icon */}
                                            </div>
                                            <input
                                                type="email"
                                                className="md-input"
                                                style={{ paddingLeft: '40px', height: '48px', borderRadius: '12px' }}
                                                placeholder="passenger@example.com"
                                                value={p.passengerEmail}
                                                onChange={e => {
                                                    const newP = [...form.passengers];
                                                    newP[idx].passengerEmail = e.target.value;
                                                    setForm({ ...form, passengers: newP });
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {/* Add another passenger button */}
                            <div style={{ gridColumn: 'span 2', display: 'flex', justifyContent: 'center' }}>
                                <button
                                    type="button"
                                    onClick={() => setForm({ ...form, passengers: [...form.passengers, { passengerName: '', passengerPhone: '', passengerEmail: '' }] })}
                                    style={{
                                        background: '#e0e7ff', color: '#4f46e5', border: '1px dashed #6366f1', borderRadius: '12px', padding: '10px 20px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s'
                                    }}
                                >
                                    + Add Another Passenger
                                </button>
                            </div>

                            {/* Location Section */}
                            <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                <label className="md-label">Pickup Point*</label>
                                <div style={{ position: 'relative' }}>
                                    <div style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#6366f1' }}>
                                        <Icon d={icons.mapPin} size={18} />
                                    </div>
                                    <input
                                        ref={pickupInputRef}
                                        className="md-input"
                                        style={{ paddingLeft: '40px', height: '48px', borderRadius: '12px', borderColor: '#e0e7ff' }}
                                        placeholder="Search pickup location..."
                                        value={form.pickupLocation}
                                        onChange={e => setForm({ ...form, pickupLocation: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                <label className="md-label">Drop Point*</label>
                                <div style={{ position: 'relative' }}>
                                    <div style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#ef4444' }}>
                                        <Icon d={icons.mapPin} size={18} />
                                    </div>
                                    <input
                                        ref={dropInputRef}
                                        className="md-input"
                                        style={{ paddingLeft: '40px', height: '48px', borderRadius: '12px', borderColor: '#fee2e2' }}
                                        placeholder="Search destination..."
                                        value={form.dropLocation}
                                        onChange={e => setForm({ ...form, dropLocation: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>

                            {/* Timing Selectors */}
                            <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                <label className="md-label">Booking Date*</label>
                                <input
                                    type="date"
                                    className="md-input"
                                    style={{ height: '48px', borderRadius: '12px' }}
                                    value={form.tripDate}
                                    onChange={e => setForm({ ...form, tripDate: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="md-form-group">
                                <label className="md-label">Pickup Time*</label>
                                <input
                                    type="time"
                                    className="md-input"
                                    style={{ height: '48px', borderRadius: '12px' }}
                                    value={form.pickupTime}
                                    onChange={e => setForm({ ...form, pickupTime: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="md-form-group">
                                <label className="md-label">Drop Time (Optional)</label>
                                <input
                                    type="time"
                                    className="md-input"
                                    style={{ height: '48px', borderRadius: '12px' }}
                                    value={form.dropTime}
                                    onChange={e => setForm({ ...form, dropTime: e.target.value })}
                                />
                            </div>

                            {/* Assign Section */}
                            <div style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', padding: '12px', background: '#f0f9ff', borderRadius: '12px', border: '1px solid #bae6fd' }}>
                                <input
                                    type="checkbox"
                                    id="vendorToggle"
                                    checked={form.isVendorMode}
                                    onChange={e => setForm({ ...form, isVendorMode: e.target.checked, vendorId: '', vehicleId: '', driverId: '' })}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <label htmlFor="vendorToggle" style={{ fontSize: '14px', fontWeight: 600, color: '#0369a1', cursor: 'pointer' }}>
                                    Assign to External Vendor?
                                </label>
                            </div>

                            {form.isVendorMode ? (
                                <>
                                    <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                        <label className="md-label">Select Vendor*</label>
                                        <select
                                            className="md-input"
                                            style={{ height: '48px', borderRadius: '12px' }}
                                            value={form.vendorId}
                                            onChange={e => setForm({ ...form, vendorId: e.target.value, vehicleId: '' })}
                                            required
                                        >
                                            <option value="">-- Choose Vendor --</option>
                                            {vendors.map(v => (
                                                <option key={v._id} value={v._id}>{v.vendorName}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                        <label className="md-label">Vendor Vehicle*</label>
                                        <select
                                            className="md-input"
                                            style={{ height: '48px', borderRadius: '12px' }}
                                            value={form.vehicleId}
                                            onChange={e => setForm({ ...form, vehicleId: e.target.value })}
                                            required
                                            disabled={!form.vendorId}
                                        >
                                            <option value="">-- Select Vendor Fleet --</option>
                                            {filteredVendorFleet.map(v => (
                                                <option key={v._id} value={v._id}>{v.registrationNumber} - {v.make}</option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="md-form-group">
                                        <label className="md-label">Select Vehicle*</label>
                                        <select
                                            className="md-input"
                                            style={{ height: '48px', borderRadius: '12px' }}
                                            value={form.vehicleId}
                                            onChange={e => handleVehicleChange(e.target.value)}
                                            required
                                        >
                                            <option value="">-- Select Vehicle --</option>
                                            {vehicles.map(v => (
                                                <option key={v._id} value={v._id}>
                                                    {v.plateNumber || v.registrationNumber}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="md-form-group">
                                        <label className="md-label">Assign Driver*</label>
                                        <select
                                            className="md-input"
                                            style={{ height: '48px', borderRadius: '12px' }}
                                            value={form.driverId}
                                            onChange={e => setForm({ ...form, driverId: e.target.value })}
                                            required
                                        >
                                            <option value="">-- Select Driver --</option>
                                            {drivers.map(d => (
                                                <option key={d._id} value={d._id}>{d.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            )}

                            {form.vehicleId && !form.isVendorMode && (
                                <div style={{ gridColumn: 'span 2', display: 'flex', gap: '15px', padding: '15px', background: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Driver</div>
                                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>
                                            {drivers.find(d => d._id === form.driverId)?.name || 'Unassigned'}
                                        </div>
                                    </div>
                                    <div style={{ width: '1px', background: '#e2e8f0' }} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Vendor</div>
                                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>
                                            {vendors.find(v => v._id === form.vendorId)?.vendorName || 'Self-Owned'}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Trip Amount Section */}
                            <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                <label className="md-label">Trip Amount / Fare</label>
                                <div style={{ position: 'relative' }}>
                                    <div style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#10b981', fontWeight: 700 }}>
                                        {COUNTRY_CONFIGS[operatorDetails?.country]?.currencySymbol || '$'}
                                    </div>
                                    <input
                                        type="number"
                                        className="md-input"
                                        style={{ paddingLeft: '35px', height: '48px', borderRadius: '12px' }}
                                        placeholder="0.00"
                                        value={form.amount}
                                        onChange={e => setForm({ ...form, amount: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>

                        <button
                            type="submit"
                            className="md-save-btn"
                            disabled={loading}
                            style={{
                                width: '100%', padding: '16px', fontSize: '16px', fontWeight: 700,
                                marginTop: '30px', borderRadius: '16px',
                                background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                                boxShadow: '0 10px 20px rgba(79, 70, 229, 0.2)',
                                display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px',
                                border: 'none', cursor: 'pointer', transition: 'all 0.3s ease'
                            }}
                        >
                            {loading ? (
                                <div className="spinner" style={{ width: 20, height: 20, border: '3px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                            ) : "Confirm Adhoc Booking"}
                        </button>
                    </form>
                </div>

                {/* Right: History Card */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div className="md-panel" style={{ padding: '0', overflow: 'hidden', borderRadius: '24px', border: '1px solid #f1f5f9' }}>
                        <div style={{ padding: '25px', borderBottom: '1px solid #f1f5f9', background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#1e1b4b', margin: 0 }}>Recent Adhoc Trips</h2>
                                <p style={{ fontSize: '13px', color: '#94a3b8', margin: '4px 0 0 0' }}>Newly created immediate bookings.</p>
                            </div>
                            <span style={{ fontSize: '11px', fontWeight: 700, background: '#eef2ff', color: '#6366f1', padding: '4px 10px', borderRadius: '20px' }}>
                                {Math.min(5, adhocTrips.length)} of {adhocTrips.length}
                            </span>
                        </div>

                        <div style={{ maxHeight: '650px', overflowY: 'auto', padding: '10px' }}>
                            {adhocTrips.length === 0 ? (
                                <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                                    <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px' }}>
                                        <Icon d={icons.truck} size={24} strokeWidth={1} />
                                    </div>
                                    <p style={{ color: '#94a3b8', fontSize: '14px' }}>No adhoc trips recorded yet.</p>
                                </div>
                            ) : (
                                [...adhocTrips]
                                    .sort((a, b) => new Date(b.createdAt || b.tripDate) - new Date(a.createdAt || a.tripDate))
                                    .slice(0, 5)
                                    .map((trip, idx) => (
                                        <div key={trip._id} style={{
                                            padding: '16px', borderRadius: '16px', marginBottom: '10px',
                                            background: '#fff', border: '1px solid #f1f5f9',
                                            display: 'flex', flexDirection: 'column', gap: '8px',
                                            transition: 'transform 0.2s', cursor: 'default'
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <div>
                                                    <div style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>{trip.passengerName}</div>
                                                    {trip.passengerEmail && (
                                                        <div style={{ fontSize: '11px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                                                            <Icon d={icons.loader} size={10} /> {trip.passengerEmail}
                                                        </div>
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', gap: '5px' }}>
                                                    <div style={{ fontSize: '10px', fontWeight: 700, color: '#6366f1', background: '#eef2ff', padding: '4px 8px', borderRadius: '20px' }}>
                                                        P: {trip.pickupTime || trip.startTime}
                                                    </div>
                                                    {trip.dropTime && (
                                                        <div style={{ fontSize: '10px', fontWeight: 700, color: '#be185d', background: '#fbcfe8', padding: '4px 8px', borderRadius: '20px' }}>
                                                            D: {trip.dropTime}
                                                        </div>
                                                    )}
                                                    <div style={{
                                                        fontSize: '10px', fontWeight: 800,
                                                        background: getStatusStyle(trip.status).bg,
                                                        color: getStatusStyle(trip.status).color,
                                                        padding: '4px 10px', borderRadius: '20px',
                                                        textTransform: 'uppercase'
                                                    }}>
                                                        {getStatusStyle(trip.status).label}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Step 1: Operator proposes price */}
                                            {trip.status === 'Requested' && (
                                                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                                    <button
                                                        onClick={() => setShowPriceModal({ ...trip, proposedPrice: '', operatorNotes: '' })}
                                                        style={{
                                                            flex: 1, padding: '8px', borderRadius: '10px',
                                                            background: 'linear-gradient(135deg, #6366f1, #4f46e5)', color: '#fff', border: 'none',
                                                            fontWeight: 700, fontSize: '13px', cursor: 'pointer'
                                                        }}
                                                    >
                                                        💰 Propose Price
                                                    </button>
                                                    <button
                                                        onClick={() => handleUpdateStatus(trip._id, 'Rejected')}
                                                        style={{
                                                            flex: 1, padding: '8px', borderRadius: '10px',
                                                            background: '#ef4444', color: '#fff', border: 'none',
                                                            fontWeight: 700, fontSize: '13px', cursor: 'pointer'
                                                        }}
                                                    >
                                                        Reject
                                                    </button>
                                                </div>
                                            )}

                                            {/* Client counter-offer: show suggestion note + re-propose */}
                                            {trip.status === 'ClientSuggested' && (
                                                <div style={{ marginTop: '10px' }}>
                                                    <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '10px', padding: '10px', marginBottom: '10px' }}>
                                                        <div style={{ fontSize: '11px', fontWeight: 800, color: '#92400e', marginBottom: '4px' }}>💬 CLIENT SUGGESTION</div>
                                                        <div style={{ fontSize: '13px', color: '#78350f' }}>{trip.clientNotes || 'Client has a counter-offer.'}</div>
                                                        <div style={{ fontSize: '11px', color: '#b45309', marginTop: '4px' }}>Original proposed: {COUNTRY_CONFIGS[operatorDetails?.country]?.currencySymbol || '$'}{trip.proposedPrice}</div>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <button
                                                            onClick={() => setShowPriceModal({ ...trip, proposedPrice: trip.proposedPrice || '', operatorNotes: '' })}
                                                            style={{ flex: 1, padding: '7px', borderRadius: '9px', background: '#6366f1', color: '#fff', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                                                        >Re-Propose Price</button>
                                                        <button
                                                            onClick={() => handleUpdateStatus(trip._id, 'ClientAccepted', { clientResponse: 'Accept' })}
                                                            style={{ flex: 1, padding: '7px', borderRadius: '9px', background: '#10b981', color: '#fff', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                                                        >Accept As-Is</button>
                                                        <button
                                                            onClick={() => handleUpdateStatus(trip._id, 'Rejected')}
                                                            style={{ flex: 1, padding: '7px', borderRadius: '9px', background: '#ef4444', color: '#fff', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                                                        >Reject</button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Step 2: Client accepted → operator assigns fleet & driver */}
                                            {trip.status === 'ClientAccepted' && (
                                                <div style={{ marginTop: '10px' }}>
                                                    <div style={{ background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '10px', padding: '10px', marginBottom: '10px' }}>
                                                        <div style={{ fontSize: '11px', fontWeight: 800, color: '#065f46' }}>✅ CLIENT ACCEPTED</div>
                                                        <div style={{ fontSize: '13px', color: '#047857', fontWeight: 600 }}>Price: {COUNTRY_CONFIGS[operatorDetails?.country]?.currencySymbol || '$'}{(trip.proposedPrice || trip.amount || 0).toLocaleString()}</div>
                                                        {trip.operatorNotes && <div style={{ fontSize: '12px', color: '#059669', marginTop: '4px' }}>Notes: {trip.operatorNotes}</div>}
                                                    </div>
                                                    <button
                                                        onClick={() => setShowAssignModal({ ...trip, isVendorMode: false, vehicleId: '', driverId: '', vendorId: '' })}
                                                        style={{ width: '100%', padding: '9px', borderRadius: '10px', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', border: 'none', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}
                                                    >
                                                        🚌 Assign Fleet & Driver
                                                    </button>
                                                </div>
                                            )}

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
                                                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#6366f1' }} />
                                                <div style={{ fontSize: '13px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                                                    {trip.pickupLocation}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444' }} />
                                                <div style={{ fontSize: '13px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                                                    {trip.dropLocation}
                                                </div>
                                            </div>

                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', paddingTop: '8px', borderTop: '1px dashed #f1f5f9' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#94a3b8' }}>
                                                    <Icon d={icons.phone} size={11} /> {trip.passengerPhone}
                                                </div>
                                                <div style={{ fontSize: '11px', fontWeight: 600, color: '#1e1b4b' }}>
                                                    {trip.amount > 0 ? formatCurrency(trip.amount, operatorDetails?.country) :
                                                        (trip.proposedPrice > 0 || trip.status === 'PriceProposed') ? formatCurrency(trip.proposedPrice || 0, operatorDetails?.country) : '—'}
                                                </div>
                                                <div style={{ fontSize: '11px', fontWeight: 600, color: '#1e1b4b' }}>
                                                    {trip.vehicleId?.plateNumber || trip.vehicleId?.registrationNumber || 'Vehicle Assigned'}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                            )}
                        </div>
                        {adhocTrips.length > 5 && (
                            <div style={{ padding: '14px 20px', borderTop: '1px solid #f1f5f9', textAlign: 'center' }}>
                                <button
                                    onClick={() => setShowAllTrips(prev => !prev)}
                                    style={{
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        color: '#6366f1', fontSize: '13px', fontWeight: 700,
                                        display: 'flex', alignItems: 'center', gap: '6px', margin: '0 auto'
                                    }}
                                >
                                    {showAllTrips ? '▲ Hide All Trips' : `▼ View All ${adhocTrips.length} Adhoc Trips`}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── All Adhoc Trips Table ── */}
            {showAllTrips && (
                <div style={{ marginTop: '30px' }}>
                    <div style={{ background: '#fff', borderRadius: '24px', border: '1px solid #f1f5f9', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.04)' }}>
                        <div style={{ padding: '22px 28px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#1e1b4b', margin: 0 }}>All Adhoc Trips</h2>
                                <p style={{ fontSize: '13px', color: '#94a3b8', margin: '4px 0 0 0' }}>Complete list of all on-demand bookings.</p>
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: 700, background: '#eef2ff', color: '#6366f1', padding: '5px 14px', borderRadius: '20px' }}>
                                {adhocTrips.length} Total
                            </span>
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #f1f5f9' }}>
                                        {['#', 'Passenger', 'Phone', 'Pickup → Drop', 'Date', 'Time', 'Amount', 'Status', 'Actions'].map(h => (
                                            <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...adhocTrips]
                                        .sort((a, b) => new Date(b.createdAt || b.tripDate) - new Date(a.createdAt || a.tripDate))
                                        .map((trip, idx) => {
                                            const st = getStatusStyle(trip.status);
                                            return (
                                                <tr key={trip._id} style={{ borderBottom: '1px solid #f8fafc', transition: 'background 0.15s' }}
                                                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                                                    <td style={{ padding: '12px 16px', color: '#94a3b8', fontWeight: 600 }}>{idx + 1}</td>
                                                    <td style={{ padding: '12px 16px', fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap' }}>
                                                        {trip.passengerName}
                                                        {trip.passengerEmail && <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 400 }}>{trip.passengerEmail}</div>}
                                                    </td>
                                                    <td style={{ padding: '12px 16px', color: '#475569', whiteSpace: 'nowrap' }}>{trip.passengerPhone}</td>
                                                    <td style={{ padding: '12px 16px', maxWidth: '260px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                                            <div style={{ marginTop: '3px' }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#6366f1', display: 'inline-block' }} /></div>
                                                            <div style={{ fontSize: '12px', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '220px' }}>{trip.pickupLocation}</div>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '3px' }}>
                                                            <div style={{ marginTop: '3px' }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} /></div>
                                                            <div style={{ fontSize: '12px', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '220px' }}>{trip.dropLocation}</div>
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '12px 16px', color: '#475569', whiteSpace: 'nowrap' }}>
                                                        {trip.tripDate ? new Date(trip.tripDate).toLocaleDateString() : '—'}
                                                    </td>
                                                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                                                        <span style={{ background: '#eef2ff', color: '#6366f1', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>P: {trip.pickupTime || trip.startTime || '—'}</span>
                                                        {trip.dropTime && <span style={{ background: '#fbcfe8', color: '#be185d', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, marginLeft: '4px' }}>D: {trip.dropTime}</span>}
                                                    </td>
                                                    <td style={{ padding: '12px 16px', fontWeight: 700, color: '#1e1b4b', whiteSpace: 'nowrap' }}>
                                                        {trip.amount > 0 ? formatCurrency(trip.amount, operatorDetails?.country) :
                                                            trip.proposedPrice > 0 ? formatCurrency(trip.proposedPrice, operatorDetails?.country) : '—'}
                                                    </td>
                                                    <td style={{ padding: '12px 16px' }}>
                                                        <span style={{ background: st.bg, color: st.color, padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                                                            {st.label}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                                                        {trip.status === 'Requested' && (
                                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                                <button onClick={() => setShowPriceModal({ ...trip, proposedPrice: '', operatorNotes: '' })}
                                                                    style={{ padding: '5px 10px', borderRadius: '8px', background: '#6366f1', color: '#fff', border: 'none', fontWeight: 600, fontSize: '11px', cursor: 'pointer' }}>💰 Price</button>
                                                                <button onClick={() => handleUpdateStatus(trip._id, 'Rejected')}
                                                                    style={{ padding: '5px 10px', borderRadius: '8px', background: '#ef4444', color: '#fff', border: 'none', fontWeight: 600, fontSize: '11px', cursor: 'pointer' }}>Reject</button>
                                                            </div>
                                                        )}
                                                        {trip.status === 'ClientAccepted' && (
                                                            <button onClick={() => setShowAssignModal({ ...trip, isVendorMode: false, vehicleId: '', driverId: '', vendorId: '' })}
                                                                style={{ padding: '5px 12px', borderRadius: '8px', background: '#10b981', color: '#fff', border: 'none', fontWeight: 600, fontSize: '11px', cursor: 'pointer' }}>🚌 Assign</button>
                                                        )}
                                                        {trip.status === 'ClientSuggested' && (
                                                            <button onClick={() => setShowPriceModal({ ...trip, proposedPrice: trip.proposedPrice || '', operatorNotes: '' })}
                                                                style={{ padding: '5px 12px', borderRadius: '8px', background: '#f59e0b', color: '#fff', border: 'none', fontWeight: 600, fontSize: '11px', cursor: 'pointer' }}>Re-Propose</button>
                                                        )}
                                                        {!['Requested', 'ClientAccepted', 'ClientSuggested'].includes(trip.status) && (
                                                            <span style={{ color: '#cbd5e1', fontSize: '12px' }}>—</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* ── MODAL 1: Operator proposes price ── */}
            {showPriceModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#fff', padding: '32px', borderRadius: '24px', width: '480px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)' }}>
                        <h3 style={{ margin: '0 0 6px 0', fontSize: '20px', fontWeight: 800, color: '#1e1b4b' }}>💰 Propose Trip Price</h3>
                        <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#64748b' }}>Set a price and optional notes — the client will review and respond.</p>

                        <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '14px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                <span style={{ fontSize: '12px', color: '#64748b' }}>Passenger:</span>
                                <span style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b' }}>{showPriceModal.passengerName}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                <span style={{ fontSize: '12px', color: '#64748b' }}>Route:</span>
                                <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>{showPriceModal.pickupLocation?.slice(0, 25)}... ➔ {showPriceModal.dropLocation?.slice(0, 25)}...</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '12px', color: '#64748b' }}>Date:</span>
                                <span style={{ fontSize: '12px', fontWeight: 700, color: '#6366f1' }}>{showPriceModal.tripDate ? new Date(showPriceModal.tripDate).toLocaleDateString() : '—'} {showPriceModal.pickupTime || showPriceModal.startTime || ''}</span>
                            </div>
                        </div>

                        <div className="md-form-group" style={{ marginBottom: '16px' }}>
                            <label className="md-label" style={{ fontSize: '13px', fontWeight: 700, color: '#374151' }}>Trip Price *</label>
                            <div style={{ position: 'relative' }}>
                                <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#10b981', fontWeight: 800, fontSize: '14px' }}>
                                    {COUNTRY_CONFIGS[operatorDetails?.country]?.currencySymbol || '$'}
                                </div>
                                <input
                                    type="number"
                                    className="md-input"
                                    style={{ paddingLeft: '30px', height: '48px', borderRadius: '12px', fontSize: '16px', fontWeight: 700 }}
                                    placeholder="0.00"
                                    value={showPriceModal.proposedPrice}
                                    onChange={e => setShowPriceModal({ ...showPriceModal, proposedPrice: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="md-form-group" style={{ marginBottom: '24px' }}>
                            <label className="md-label" style={{ fontSize: '13px', fontWeight: 700, color: '#374151' }}>Operator Notes (Optional)</label>
                            <textarea
                                className="md-input"
                                rows={3}
                                style={{ resize: 'vertical', padding: '12px', borderRadius: '12px', fontSize: '13px', lineHeight: 1.5 }}
                                placeholder="Add any notes for the client (inclusions, conditions, etc.)"
                                value={showPriceModal.operatorNotes}
                                onChange={e => setShowPriceModal({ ...showPriceModal, operatorNotes: e.target.value })}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button onClick={() => setShowPriceModal(null)} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#fff', fontWeight: 600, cursor: 'pointer', color: '#6b7280' }}>Cancel</button>
                            <button
                                onClick={async () => {
                                    if (!showPriceModal.proposedPrice) { alert('Please enter a price.'); return; }
                                    await handleUpdateStatus(showPriceModal._id, 'PriceProposed', {
                                        proposedPrice: Number(showPriceModal.proposedPrice),
                                        operatorNotes: showPriceModal.operatorNotes,
                                        clientResponse: ''
                                    });
                                    setShowPriceModal(null);
                                }}
                                style={{ flex: 2, padding: '12px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #6366f1, #4f46e5)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '14px' }}
                            >
                                📨 Send Price to Client
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── MODAL 2: Assign fleet & driver (after client accepted) ── */}
            {showAssignModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#fff', padding: '30px', borderRadius: '24px', width: '450px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                        <h3 style={{ margin: '0 0 6px 0', fontSize: '20px', fontWeight: 800, color: '#065f46' }}>🚌 Assign Fleet & Driver</h3>
                        <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#64748b' }}>Client accepted the price. Now confirm fleet assignment to schedule the trip.</p>

                        <div style={{ background: '#ecfdf5', padding: '12px', borderRadius: '12px', border: '1px solid #6ee7b7', marginBottom: '18px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                <span style={{ fontSize: '12px', color: '#047857' }}>Passenger:</span>
                                <span style={{ fontSize: '12px', fontWeight: 700, color: '#065f46' }}>{showAssignModal.passengerName}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: '12px', color: '#047857' }}>Agreed Price:</span>
                                <span style={{ fontSize: '12px', fontWeight: 800, color: '#059669' }}>{COUNTRY_CONFIGS[operatorDetails?.country]?.currencySymbol || '$'}{showAssignModal.proposedPrice}</span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', padding: '10px', background: '#f0f9ff', borderRadius: '10px', border: '1px solid #bae6fd' }}>
                            <input type="checkbox" id="assignVendorToggle" checked={showAssignModal.isVendorMode}
                                onChange={e => setShowAssignModal({ ...showAssignModal, isVendorMode: e.target.checked, vendorId: '', vehicleId: '', driverId: '' })}
                                style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                            <label htmlFor="assignVendorToggle" style={{ fontSize: '13px', fontWeight: 600, color: '#0369a1', cursor: 'pointer' }}>Assign to External Vendor?</label>
                        </div>

                        {showAssignModal.isVendorMode ? (
                            <>
                                <div className="md-form-group" style={{ marginBottom: '14px' }}>
                                    <label className="md-label">Select Vendor*</label>
                                    <select className="md-input" style={{ height: '44px', borderRadius: '10px' }}
                                        value={showAssignModal.vendorId} onChange={e => setShowAssignModal({ ...showAssignModal, vendorId: e.target.value, vehicleId: '' })}>
                                        <option value="">-- Choose Vendor --</option>
                                        {vendors.map(v => <option key={v._id} value={v._id}>{v.vendorName}</option>)}
                                    </select>
                                </div>
                                <div className="md-form-group" style={{ marginBottom: '14px' }}>
                                    <label className="md-label">Vendor Vehicle*</label>
                                    <select className="md-input" style={{ height: '44px', borderRadius: '10px' }}
                                        value={showAssignModal.vehicleId} onChange={e => setShowAssignModal({ ...showAssignModal, vehicleId: e.target.value })} disabled={!showAssignModal.vendorId}>
                                        <option value="">-- Select Vendor Fleet --</option>
                                        {vendorFleet.filter(vf => vf.vendorId?._id === showAssignModal.vendorId || vf.vendorId === showAssignModal.vendorId).map(v => (
                                            <option key={v._id} value={v._id}>{v.registrationNumber} - {v.make}</option>
                                        ))}
                                    </select>
                                </div>
                            </>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                                <div className="md-form-group">
                                    <label className="md-label">Vehicle*</label>
                                    <select className="md-input" style={{ height: '44px', borderRadius: '10px' }}
                                        value={showAssignModal.vehicleId}
                                        onChange={e => {
                                            const vId = e.target.value;
                                            const v = vehicles.find(v => v._id === vId);
                                            setShowAssignModal({ ...showAssignModal, vehicleId: vId, driverId: v?.driverId?._id || v?.driverId || showAssignModal.driverId });
                                        }}>
                                        <option value="">-- Select --</option>
                                        {vehicles.map(v => <option key={v._id} value={v._id}>{v.plateNumber || v.registrationNumber}</option>)}
                                    </select>
                                </div>
                                <div className="md-form-group">
                                    <label className="md-label">Driver*</label>
                                    <select className="md-input" style={{ height: '44px', borderRadius: '10px' }}
                                        value={showAssignModal.driverId} onChange={e => setShowAssignModal({ ...showAssignModal, driverId: e.target.value })}>
                                        <option value="">-- Select --</option>
                                        {drivers.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                            <button onClick={() => setShowAssignModal(null)} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#fff', fontWeight: 600, cursor: 'pointer', color: '#6b7280' }}>Cancel</button>
                            <button
                                onClick={() => handleUpdateStatus(showAssignModal._id, 'Scheduled', {
                                    vehicleId: showAssignModal.vehicleId,
                                    driverId: showAssignModal.driverId,
                                    vendorId: showAssignModal.vendorId,
                                    isVendorMode: showAssignModal.isVendorMode,
                                    amount: showAssignModal.proposedPrice
                                })}
                                disabled={!showAssignModal.vehicleId || (!showAssignModal.isVendorMode && !showAssignModal.driverId)}
                                style={{ flex: 2, padding: '12px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '14px', opacity: (showAssignModal.vehicleId && (showAssignModal.isVendorMode || showAssignModal.driverId)) ? 1 : 0.5 }}
                            >
                                ✅ Confirm & Schedule
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
                .md-form-group { display: flex; flex-direction: column; gap: 8px; }
                .md-input:focus { border-color: #6366f1 !important; box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1) !important; outline: none; }
                .md-save-btn:hover { transform: translateY(-2px); box-shadow: 0 15px 30px rgba(79, 70, 229, 0.3); opacity: 0.95; }
                .md-save-btn:active { transform: translateY(1px); }
            `}</style>
        </div>
    );
}

